import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useBOMSetupCityListAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupCityListAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import CheckBox from "@/app/components/ui/CheckBoxes/FormCheckbox";
import Input from "@/app/components/ui/Inputs/Input";
import useFormValidations from "@/app/hooks/useFormValidations";
import { FaArrowDown } from "react-icons/fa6";
import { useConfigurationClinicListTableAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicListTableAPI";
import { useLocation, useNavigate } from "react-router";
import { IConfigurationServiceOnboardPostBody } from "@/api_framework/api_modals/Configuration";
import { useSearchParams } from "react-router-dom";
import { useConfigurationServiceUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationServiceUpdateAPI";
import { useConfigurationServiceDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationServiceDetailAPI";
import toast from "react-hot-toast";
import clsx from "clsx";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

export interface IServiceFormClinicMappingFields {
  status: boolean;
  city: string[];
  display: { [clinicId: string]: boolean };
  allDisplay: boolean;
  price: { [clinicId: string]: number };
}

interface IClinicPricingEntry {
  clinic_id?: string;
  clinic_name?: string;
  selling_price?: number | string;
  price?: number | string;
}

const ClinicPricing = () => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { cities } = routeState.state as IInventoryState;
  const safeCities = Array.isArray(cities) ? cities : [];
  const cityItems = useSelectMenuReducer(safeCities, "name", "id");
  const [cliniclist, setClinicList] = useState([]);
  const [persistentDisplay, setPersistentDisplay] = useState<Record<string, boolean>>({});
  const appliedEditClinicPricingKeyRef = useRef<string>("");
  const [bomCities, setBomCities] = useState<any>();
  const { execute: fetchBomCityList, bomCityList } = useBOMSetupCityListAPI();
  const fetchOrgCities = useOrgCitiesListAPI("INVENTORY");
  const { forOnlyNumbers } = useFormValidations();
  const fetchClinicList = useConfigurationClinicListTableAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const savedGeneralDetails = location.state ?? null;
  const [params, _setparams] = useSearchParams();
  const { execute: updateServiceAPI } = useConfigurationServiceUpdateAPI();
  const { execute: fetchServiceDetailForEdit, serviceDetails: editServiceDetails } = useConfigurationServiceDetailAPI();
  const serviceID = params.get("service_id");
  const subServiceID = params.get("sub_service_id");
  const isEditMode = Boolean(subServiceID);

  const formHook = useForm<IServiceFormClinicMappingFields>({
    mode: "onChange",
    defaultValues: {},
  });

  const parsePrice = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const getSavedClinicPricing = (): IClinicPricingEntry[] => {
    if (isEditMode) {
      const apiClinicPricing = editServiceDetails?.clinic_pricing;
      return Array.isArray(apiClinicPricing) ? apiClinicPricing : [];
    }
    const clinicPricingRaw =
      savedGeneralDetails?.generalDetails?.serviceDetails?.clinic_pricing ||
      savedGeneralDetails?.serviceDetails?.clinic_pricing;
    return Array.isArray(clinicPricingRaw) ? clinicPricingRaw : [];
  };

  const updatePersistentDisplay = (clinicId: string, value: boolean) => {
    setPersistentDisplay((prev) => ({
      ...prev,
      [clinicId]: value,
    }));
  };

  const onCheckChangeHandler = (clinicId: string, value: boolean) => {
    formHook.setValue(`display.${clinicId}`, value);
    updatePersistentDisplay(clinicId, value);
  };

  useEffect(() => {
    fetchOrgCities();
    fetchClinicList();
  }, []);

  useEffect(() => {
    if (isEditMode && serviceID && subServiceID) {
      fetchServiceDetailForEdit(serviceID, subServiceID);
    }
  }, [isEditMode, serviceID, subServiceID, fetchServiceDetailForEdit]);

  useEffect(() => {
    if (safeCities.length > 0) {
      const allCityIds = safeCities.map((c) => c.id);
      formHook.setValue("city", allCityIds);
      fetchBomCityList(allCityIds);
    }
  }, [safeCities]);

  useEffect(() => {
    const safeBomCityList = Array.isArray(bomCityList) ? bomCityList : [];
    if (safeBomCityList.length > 0) {
      if (cliniclist.length === 0) {
        setClinicList(safeBomCityList);
      }
      const updatedCities = safeBomCityList.map((city) => {
        const existingDisplay = persistentDisplay[city.clinic_id];
        return {
          ...city,
          display: existingDisplay !== undefined ? existingDisplay : false,
        };
      });
      setBomCities(updatedCities);
    }
  }, [bomCityList]);

  useEffect(() => {
    // In edit mode, checkbox defaults should come only from API clinic_pricing
    if (!isEditMode) {
      return;
    }

    const clinicPricing = getSavedClinicPricing();
    const safeBomCityList = Array.isArray(bomCityList) ? bomCityList : [];
    if (!editServiceDetails || !safeBomCityList.length) return;
    const pricingKey = clinicPricing
      .map((item) => `${item?.clinic_id || ""}:${parsePrice(item?.selling_price ?? item?.price)}`)
      .sort()
      .join("|");
    const editApiSyncKey = `${serviceID || ""}_${subServiceID || ""}_${pricingKey}`;
    if (appliedEditClinicPricingKeyRef.current === editApiSyncKey) {
      return;
    }

    const updated = safeBomCityList.map((clinic) => {
      const found = clinicPricing.find((m) => m.clinic_id === clinic.clinic_id);
      const priceFromApi = found?.selling_price ?? found?.price ?? 0;
      return {
        ...clinic,
        display: found ? true : false,
        price: found ? priceFromApi : 0,
      };
    });
    setBomCities(updated);

    const displayObj: Record<string, boolean> = {};
    const priceObj: Record<string, number> = {};
    updated.forEach((c) => {
      displayObj[c.clinic_id] = c.display;
      priceObj[c.clinic_id] = c.price;
    });
    setPersistentDisplay(displayObj);

    // Set form values with reset to ensure they take effect
    setTimeout(() => {
      formHook.reset({
        display: displayObj,
        price: priceObj,
      });
    }, 0);
    appliedEditClinicPricingKeyRef.current = editApiSyncKey;
  }, [bomCityList, serviceID, isEditMode, editServiceDetails]);

  useEffect(() => {
    if (isEditMode) return;
    if (!location.state) return;
    if (location.state.clinicPricingState) {
      formHook.reset(location.state.clinicPricingState);
    }
  }, [location.state?.clinicPricingState, isEditMode]);

  const handleCancel = () => {
    if (!isEditMode) {
      // Pass the current clinic pricing state back so it persists when returning
      window.history.replaceState({ ...window.history.state, clinicPricingState: formHook.getValues() }, "");
    }
    // Maintain editMode flag so general form can restore edited values
    window.history.back();
  };

  const onSubmit = formHook.handleSubmit((clinicPricingData) => {
    // if (!savedGeneralDetails) return;
    const mergedDisplay = {
      ...persistentDisplay,
      ...(clinicPricingData.display || {}),
    };
    const selectedClinicIds = Object.entries(mergedDisplay)
      .filter(([, isSelected]) => Boolean(isSelected))
      .map(([clinicId]) => clinicId);

    const savedClinicPricing = getSavedClinicPricing();
    const clinicNameById = new Map<string, string>();
    const defaultPriceById = new Map<string, number>();

    (Array.isArray(cliniclist) ? cliniclist : []).forEach((clinic) => {
      if (clinic?.clinic_id) {
        clinicNameById.set(clinic.clinic_id, clinic?.clinic_name || "");
      }
    });

    (Array.isArray(bomCities) ? bomCities : []).forEach((clinic) => {
      if (clinic?.clinic_id) {
        clinicNameById.set(clinic.clinic_id, clinic?.clinic_name || clinicNameById.get(clinic.clinic_id) || "");
        defaultPriceById.set(clinic.clinic_id, parsePrice(clinic?.price));
      }
    });

    savedClinicPricing.forEach((clinic) => {
      if (clinic?.clinic_id) {
        clinicNameById.set(clinic.clinic_id, clinic?.clinic_name || clinicNameById.get(clinic.clinic_id) || "");
        defaultPriceById.set(clinic.clinic_id, parsePrice(clinic?.selling_price ?? clinic?.price));
      }
    });

    const clinic_pricing = selectedClinicIds.map((clinicId) => ({
      clinic_id: clinicId,
      clinic_name: clinicNameById.get(clinicId) || "",
      selling_price: parsePrice(clinicPricingData.price?.[clinicId] ?? defaultPriceById.get(clinicId)),
    }));

    const generalDetails = savedGeneralDetails?.generalDetails;
    const resolvedServiceId = params.get("service_id") || generalDetails?.serviceID || null;
    const resolvedSubServiceId = params.get("sub_service_id") || generalDetails?.l2Service || null;
    if (!resolvedServiceId || !resolvedSubServiceId) {
      toast.error("service_id and sub_service_id are required for clinic section");
      return;
    }
    const constructedBody: IConfigurationServiceOnboardPostBody = {
      service_details: {
        service_id: resolvedServiceId,
        service_name: generalDetails?.serviceName || "",
      },
      sub_service_details: {
        sub_service_name: generalDetails?.l2ServiceName || "",
        sub_service_id: resolvedSubServiceId,
      },
      clinic_pricing,
    };
    updateServiceAPI(constructedBody, "clinic_pricing").then(({ status }) => {
      if (status) {
        sessionStorage.removeItem("serviceSetupDraftIds");
        navigate(`/app/inventory/service-list`);
      }
    });
  });

  return (
    <>
      <div className="bg-white rounded-md p-3">
        <h2 className="text-sm font-semibold">Service Setup</h2>
      </div>
      <form onSubmit={onSubmit}>
        <div className="bg-white mt-2 rounded-md">
          <div className="flex gap-5 p-3">
            <div className="w-80">
              <SearchableMultiSelectMenu
                withSelectAll
                errorMessages={[]}
                onSelectItem={(item) => {
                  if (Array.isArray(item)) {
                    formHook.setValue(
                      "city",
                      item.map((c) => c.id),
                    );
                  }
                }}
                fieldError={formHook.formState.errors.city}
                register={formHook.register("city")}
                selectItems={cityItems}
                placeholder="Select City"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={cityItems?.filter((c) =>
                  Array.isArray(formHook.watch("city")) ? formHook.watch("city").includes(c.id) : false,
                )}
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
            <div>
              <input
                type="button"
                onClick={() => {
                  fetchBomCityList(formHook.watch("city"));
                }}
                className={`py-2 px-6 border-transparent rounded-md text-sm cursor-pointer bg-black text-white`}
                value={"Apply Filter"}
              />
            </div>
          </div>
          <div className="p-3 bg-white sm:px-3 lg:px-3">
            <div className="shadow-md mt-2">
              <table className="divide-y divide-gray-200 border rounded-md w-full">
                <thead className="bg-[#f5f5f5] sticky top-0">
                  <tr>
                    <th className="py-3 pl-3 text-left whitespace-nowrap text-sm text-[#020202] font-medium">
                      <div className="flex gap-2 items-center">
                        <CheckBox
                          errorMessages={[]}
                          fieldError={formHook.formState.errors.display}
                          register={formHook.register("allDisplay")}
                          onCheckChange={(item) => {
                            const obj = {};
                            (Array.isArray(cliniclist) ? cliniclist : []).map((c) => (obj[c.clinic_id] = true));
                            if (item) {
                              formHook.setValue("display", obj);
                            } else {
                              const obj = {};
                              (Array.isArray(cliniclist) ? cliniclist : []).map((c) => (obj[c.clinic_id] = false));
                              formHook.setValue("display", obj);
                            }
                            formHook.setValue("allDisplay", !!item);
                          }}
                        />
                        <p>Clinic Name</p>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-[#020202] font-medium">City</th>
                    <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-[#020202] font-medium">
                      Add Price
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-orange-100">
                  {(Array.isArray(bomCities) ? bomCities : []).map((city, index) => (
                    <tr key={city.clinic_id}>
                      <td className={"pl-3 py-4 text-xs whitespace-nowrap font-normal text-left"}>
                        <div className="flex gap-2 items-center">
                          <CheckBox
                            errorMessages={[]}
                            onCheckChange={(item) => onCheckChangeHandler(city.clinic_id, item)}
                            fieldError={formHook.formState.errors.display}
                            register={formHook.register(`display.${city.clinic_id}`)}
                          />
                          <p>{city.clinic_name}</p>
                        </div>
                      </td>
                      <td className={"px-3 py-4 text-xs whitespace-nowrap font-normal text-left"}>{city.city_name}</td>
                      <td
                        className={
                          "px-3 py-4 text-xs whitespace-nowrap font-normal text-left flex gap-10 items-baseline"
                        }
                      >
                        <Input
                          register={formHook?.register(`price.${city.clinic_id}`, {
                            ...forOnlyNumbers.validations,
                            valueAsNumber: true,
                          })}
                          type="number"
                          min={0}
                          step="any"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            formHook.setValue(`price.${city.clinic_id}` as any, event.target.valueAsNumber);
                          }}
                          fieldError={formHook?.formState?.errors?.price}
                          errorMessages={[forOnlyNumbers.errors]}
                          className="border-slate-400 rounded-md w-[150px] h-9 text-sm"
                          placeholder="Enter Price"
                        />
                        {index == 0 && (
                          <button
                            type="button"
                            className="flex gap-2"
                            onClick={() => {
                              const copyPrice = formHook.watch(`price.${city.clinic_id}`);
                              const selectedClinics = formHook.watch("display") || {};
                              (Array.isArray(bomCities) ? bomCities : []).map((b) => {
                                if (selectedClinics?.[b.clinic_id]) {
                                  formHook.setValue(`price.${b.clinic_id}`, copyPrice);
                                }
                              });
                            }}
                          >
                            <FaArrowDown className="w-4 h-4" color="red" />
                            <p className="text-sm">Price applied on selected clinics</p>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 sm:justify-end mt-4">
            <button type="button" className={clsx(SECONDARY_BUTTON, "w-full sm:w-auto")} onClick={handleCancel}>
              Back
            </button>
            <button type="submit" className={clsx(PRIMARY_BUTTON, "w-full sm:w-auto")}>
              Save
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ClinicPricing;
